import React, { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Check } from "lucide-react";
import { TID } from "../../lib/testIds";

export const LightboxModal = ({ structures, openIdx, onClose, onPrev, onNext }) => {
  const isOpen = openIdx !== null && openIdx >= 0 && openIdx < structures.length;
  const item = isOpen ? structures[openIdx] : null;

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    document.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose, onPrev, onNext]);

  return (
    <AnimatePresence>
      {isOpen && item && (
        <motion.div
          data-testid={TID.modal.root}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-50 bg-slate-900/85 backdrop-blur-md flex items-center justify-center p-4 md:p-8"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 10 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-6xl bg-white rounded-2xl overflow-hidden flex flex-col lg:flex-row max-h-[92vh] shadow-2xl"
          >
            {/* Close */}
            <button
              onClick={onClose}
              data-testid={TID.modal.close}
              className="absolute top-4 right-4 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-white/90 hover:bg-white shadow-md backdrop-blur transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-slate-800" strokeWidth={2.5} />
            </button>

            {/* Nav buttons */}
            <button
              onClick={onPrev}
              data-testid={TID.modal.prev}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-11 h-11 flex items-center justify-center rounded-full bg-white/90 hover:bg-white shadow-md transition-colors"
              aria-label="Previous"
            >
              <ChevronLeft className="w-5 h-5 text-slate-800" strokeWidth={2.5} />
            </button>
            <button
              onClick={onNext}
              data-testid={TID.modal.next}
              className="absolute right-4 lg:right-[calc(41.666%+1rem)] top-1/2 -translate-y-1/2 z-20 w-11 h-11 flex items-center justify-center rounded-full bg-white/90 hover:bg-white shadow-md transition-colors"
              aria-label="Next"
            >
              <ChevronRight className="w-5 h-5 text-slate-800" strokeWidth={2.5} />
            </button>

            {/* Image */}
            <div className="relative w-full lg:w-[58.333%] bg-slate-900 aspect-[4/3] lg:aspect-auto flex-shrink-0">
              <img
                src={item.image_url}
                alt={item.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-4 left-4 inline-flex items-center gap-2 bg-white/95 backdrop-blur px-3 py-1.5 rounded-full text-[11px] font-mono uppercase tracking-[0.16em] text-slate-800">
                <span className="w-1.5 h-1.5 rounded-full bg-[#0A2540]" />
                {item.category}
              </div>
              <div className="absolute bottom-4 left-4 font-mono text-xs uppercase tracking-[0.22em] text-white/70">
                {String(openIdx + 1).padStart(2, "0")} / {String(structures.length).padStart(2, "0")}
              </div>
            </div>

            {/* Content */}
            <div className="w-full lg:w-[41.666%] p-6 md:p-8 lg:p-10 flex flex-col overflow-y-auto">
              <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#0A2540] mb-2">
                {item.tag}
              </div>
              <h3
                data-testid={TID.modal.title}
                className="font-display text-3xl md:text-4xl font-semibold text-[#0A2540] leading-[1.05] tracking-tight"
              >
                {item.title}
              </h3>

              <p className="mt-4 text-slate-600 text-base leading-relaxed">
                {item.description}
              </p>

              {/* Use cases */}
              <div className="mt-6">
                <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-slate-500 mb-3">
                  Typical Use Cases
                </div>
                <ul className="space-y-2">
                  {item.use_cases.map((u) => (
                    <li key={u} className="flex items-start gap-2.5 text-sm text-slate-700">
                      <span className="flex-shrink-0 mt-0.5 w-4 h-4 rounded-full bg-slate-100 flex items-center justify-center">
                        <Check className="w-2.5 h-2.5 text-[#0A2540]" strokeWidth={3} />
                      </span>
                      {u}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Specs */}
              <div className="mt-6 pt-6 border-t border-slate-100">
                <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-slate-500 mb-3">
                  Key Specifications
                </div>
                <dl className="grid grid-cols-1 gap-2">
                  {Object.entries(item.specs).map(([k, v]) => (
                    <div
                      key={k}
                      className="flex items-center justify-between gap-4 py-2 border-b border-dashed border-slate-100 last:border-0"
                    >
                      <dt className="text-xs uppercase tracking-wider text-slate-500 font-mono">
                        {k.replace(/_/g, " ")}
                      </dt>
                      <dd className="text-sm font-semibold text-[#0A2540] text-right">
                        {String(v)}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>

              <div className="mt-auto pt-8" />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
